import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner, PageError } from '@/components/ui';
import type { CleaningSector, CleaningEquipmentItem, CleaningTask } from '@haccp/api-client';
import { getSectorIcon, getSectorColor } from '@/utils/equipment';
import { useTranslation } from 'react-i18next';

const SECTOR_ICONS = [
  '🍳', '👨‍🍳', '🚽', '📦', '🚿', '📮',
  '🧹', '🧴', '🧺', '🗑️', '🪣', '🧼',
  '🍽️', '🥘', '🫙', '❄️', '🔥', '🪴',
  '🚪', '🏪', '🏢', '🛒', '🚚', '📋',
];

type Tab = 'plan' | 'sectors';

export default function CleaningPage() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { t, i18n } = useTranslation();

  const [tab, setTab] = useState<Tab>('plan');
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(new Set());
  const [expandedManagerSectors, setExpandedManagerSectors] = useState<Set<string>>(new Set());
  const [addingTaskFor, setAddingTaskFor] = useState<string | null>(null);
  const [newTaskName, setNewTaskName] = useState('');
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editTaskName, setEditTaskName] = useState('');
  const [newSectorName, setNewSectorName] = useState('');
  const [newSectorIcon, setNewSectorIcon] = useState('🍳');
  const [showIconPicker, setShowIconPicker] = useState(false);

  // ─── Daily plan ────────────────────────────────────
  const { data, isLoading, error } = useQuery({
    queryKey: ['cleaning-today'],
    queryFn: () => apiClient.getCleaningToday(),
    refetchInterval: 30000,
  });

  const plan = data?.plan;
  const sectors = data?.sectors || [];
  const isCompleted = !!plan?.completedAt;

  let totalTasks = 0;
  let checkedTasks = 0;
  sectors.forEach((s: CleaningSector) => {
    s.subSectors?.forEach((ss) => {
      ss.equipment?.forEach((eq: CleaningEquipmentItem) => {
        eq.tasks?.forEach((task: CleaningTask) => {
          totalTasks++;
          if (task.checked) checkedTasks++;
        });
      });
    });
  });
  const progress = totalTasks > 0 ? Math.round((checkedTasks / totalTasks) * 100) : 0;
  const allChecked = totalTasks > 0 && checkedTasks === totalTasks;

  const checkMutation = useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => apiClient.checkCleaningTask(plan!.id, taskId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cleaning-today'] }),
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.taskError'), 'error'),
  });

  const uncheckMutation = useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => apiClient.uncheckCleaningTask(plan!.id, taskId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cleaning-today'] }),
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.taskError'), 'error'),
  });

  const completeMutation = useMutation({
    mutationFn: () => apiClient.completeCleaningPlan(plan!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-today'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      addToast(t('cleaning.success'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.taskError'), 'error'),
  });

  // ─── Sector management ─────────────────────────────
  const { data: allSectors = [], isLoading: sectorsLoading } = useQuery<CleaningSector[]>({
    queryKey: ['cleaning-sectors'],
    queryFn: () => apiClient.getCleaningSectors(),
    enabled: tab === 'sectors',
  });

  const createSectorMutation = useMutation({
    mutationFn: ({ name, icon }: { name: string; icon: string }) =>
      apiClient.createCleaningSector(name, icon),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-sectors'] });
      queryClient.invalidateQueries({ queryKey: ['cleaning-today'] });
      setNewSectorName('');
      setNewSectorIcon('🍳');
      setShowIconPicker(false);
      addToast(t('cleaning.sectorAdded'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('common.error'), 'error'),
  });

  const deleteSectorMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteCleaningSector(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-sectors'] });
      queryClient.invalidateQueries({ queryKey: ['cleaning-today'] });
      addToast(t('cleaning.sectorDeleted'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.deleteError'), 'error'),
  });

  const addTaskMutation = useMutation({
    mutationFn: ({ sectorId, name }: { sectorId: string; name: string }) =>
      apiClient.addTaskToSector(sectorId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-sectors'] });
      queryClient.invalidateQueries({ queryKey: ['cleaning-today'] });
      setNewTaskName('');
      setAddingTaskFor(null);
      addToast(t('cleaning.taskAdded'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('common.error'), 'error'),
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteCleaningTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-sectors'] });
      queryClient.invalidateQueries({ queryKey: ['cleaning-today'] });
      addToast(t('cleaning.taskDeleted'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.deleteError'), 'error'),
  });

  const renameTaskMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiClient.updateCleaningTask(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-sectors'] });
      queryClient.invalidateQueries({ queryKey: ['cleaning-today'] });
      setEditingTaskId(null);
      addToast(t('cleaning.taskUpdated'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('common.error'), 'error'),
  });

  // ─── Helpers ───────────────────────────────────────
  const toggleSector = (id: string) => {
    const s = new Set(expandedSectors);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedSectors(s);
  };

  const toggleManagerSector = (id: string) => {
    const s = new Set(expandedManagerSectors);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedManagerSectors(s);
  };

  const handleToggleTask = (task: CleaningTask) => {
    if (isCompleted) return;
    if (task.checked) uncheckMutation.mutate({ taskId: task.id });
    else checkMutation.mutate({ taskId: task.id });
  };

  const today = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const isMonday = new Date().getDay() === 1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-0">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-3 bg-gray-100 rounded-lg">
              <span className="text-2xl">🧹</span>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('cleaning.planTitle')}</h1>
              <p className="text-gray-600 mt-0.5 capitalize">{today}</p>
              {isMonday && tab === 'plan' && (
                <p className="text-sm text-purple-600 font-medium">{t('cleaning.monday')}</p>
              )}
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="flex gap-1">
            <button
              onClick={() => setTab('plan')}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                tab === 'plan'
                  ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              📋 {t('cleaning.todayPlan')}
            </button>
            <button
              onClick={() => setTab('sectors')}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                tab === 'sectors'
                  ? 'border-orange-500 text-orange-600 bg-orange-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              ⚙️ {t('cleaning.manageSectors')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ════════════ TAB: PLAN DU JOUR ════════════ */}
        {tab === 'plan' && (
          <>
            {isLoading ? (
              <LoadingSpinner message={t('cleaning.loadingPlan')} />
            ) : error ? (
              <PageError message={t('cleaning.planError')} />
            ) : sectors.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-4 sm:p-8 text-center">
                <span className="text-5xl block mb-4">📋</span>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{t('cleaning.noTasksTitle')}</h2>
                <p className="text-gray-500">{t('cleaning.noTasksAdmin')}</p>
                <button
                  onClick={() => setTab('sectors')}
                  className="mt-4 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  ⚙️ {t('cleaning.manageSectors')}
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {!isCompleted && (
                  <div className="bg-white rounded-xl shadow p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-gray-700">{t('cleaning.progress')}</span>
                      <span className={`text-lg font-bold ${progress >= 100 ? 'text-green-600' : progress >= 50 ? 'text-amber-600' : 'text-blue-600'}`}>
                        {progress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${progress >= 100 ? 'bg-green-500' : progress >= 50 ? 'bg-amber-500' : 'bg-blue-500'}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{t('cleaning.tasksCompleted', { checked: checkedTasks, total: totalTasks })}</p>
                  </div>
                )}

                {isCompleted && (
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-5 flex items-center gap-3">
                    <span className="text-3xl">✅</span>
                    <div>
                      <p className="font-bold text-green-800">{t('cleaning.completedBanner')}</p>
                      <p className="text-sm text-green-600">
                        {t('cleaning.completedAt', { date: new Date(plan!.completedAt!).toLocaleString(i18n.language) })}
                      </p>
                    </div>
                  </div>
                )}

                {sectors.map((sector: CleaningSector) => {
                  const sectorChecked = sector.subSectors?.reduce((sum, ss) =>
                    sum + (ss.equipment?.reduce((s2, eq) =>
                      s2 + (eq.tasks?.filter((tk) => tk.checked).length || 0), 0) || 0), 0) || 0;
                  const sectorTotal = sector.subSectors?.reduce((sum, ss) =>
                    sum + (ss.equipment?.reduce((s2, eq) =>
                      s2 + (eq.tasks?.length || 0), 0) || 0), 0) || 0;
                  const sectorDone = sectorTotal > 0 && sectorChecked === sectorTotal;
                  const isExpanded = expandedSectors.has(sector.id);
                  const icon = sector.icon || getSectorIcon(sector.name);
                  const color = getSectorColor(sector.name);

                  return (
                    <div key={sector.id} className={`bg-white rounded-xl shadow overflow-hidden border-l-4 ${sectorDone ? 'border-l-green-400' : color.border}`}>
                      <div
                        className={`flex items-center justify-between p-5 cursor-pointer transition ${sectorDone ? 'bg-green-50/50' : 'hover:bg-gray-50'}`}
                        onClick={() => toggleSector(sector.id)}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                          <span className="text-xl">{icon}</span>
                          <h3 className="text-lg font-semibold text-gray-900">{sector.name}</h3>
                          {sectorDone && <span className="text-green-600">✓</span>}
                        </div>
                        <span className={`text-sm font-medium px-3 py-1 rounded-full ${sectorDone ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {sectorChecked}/{sectorTotal}
                        </span>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-200 p-5 space-y-5">
                          {sector.subSectors?.flatMap((ss) => ss.equipment || []).map((eq: CleaningEquipmentItem) => (
                            <div key={eq.id}>
                              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <span className="text-base">🔧</span> {eq.name}
                              </h4>
                              <div className="space-y-2">
                                {eq.tasks?.map((task: CleaningTask) => (
                                  <label
                                    key={task.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                                      task.checked ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'
                                    } ${isCompleted ? 'cursor-not-allowed opacity-75' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!task.checked}
                                      onChange={() => handleToggleTask(task)}
                                      disabled={isCompleted || checkMutation.isPending || uncheckMutation.isPending}
                                      className="w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500"
                                    />
                                    <span className={`flex-1 text-sm ${task.checked ? 'text-green-700 line-through' : 'text-gray-800'}`}>
                                      {task.name}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${task.frequency === 'DAILY' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                                      {task.frequency === 'DAILY' ? t('cleaning.daily') : t('cleaning.weekly')}
                                    </span>
                                    {task.check && (
                                      <span className="text-xs text-gray-400">
                                        {task.check.checkedBy?.displayName || task.check.checkedBy?.email}
                                      </span>
                                    )}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {!isCompleted && (
                  <button
                    onClick={() => completeMutation.mutate()}
                    disabled={!allChecked || completeMutation.isPending}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                      allChecked ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {completeMutation.isPending
                      ? t('cleaning.completing')
                      : allChecked
                        ? t('cleaning.validateButton')
                        : t('cleaning.tasksRemaining', { count: totalTasks - checkedTasks })}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* ════════════ TAB: SECTEURS ════════════ */}
        {tab === 'sectors' && (
          <div className="space-y-4">
            {sectorsLoading ? (
              <LoadingSpinner message={t('cleaning.loading')} />
            ) : (
              <>
                {allSectors.map((sector: CleaningSector) => {
                  const allTasks = (sector.subSectors ?? []).flatMap((ss) =>
                    (ss.equipment ?? []).flatMap((eq) => eq.tasks ?? [])
                  );
                  const isOpen = expandedManagerSectors.has(sector.id);
                  const color = getSectorColor(sector.name);
                  const icon = getSectorIcon(sector.name);

                  return (
                    <div key={sector.id} className={`bg-white rounded-xl shadow overflow-hidden border-l-4 ${color.border}`}>
                      {/* Sector header */}
                      <div
                        className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50 transition"
                        onClick={() => toggleManagerSector(sector.id)}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`transform transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                          <span className="text-xl">{sector.icon || icon}</span>
                          <h3 className="text-lg font-semibold text-gray-900">{sector.name}</h3>
                          {sector.isSystemZone && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">{t('cleaning.systemZone')}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                            {allTasks.length} {t('cleaning.taskCount')}
                          </span>
                          {!sector.isSystemZone && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(t('cleaning.confirmDeleteSector'))) deleteSectorMutation.mutate(sector.id);
                              }}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Task list */}
                      {isOpen && (
                        <div className="border-t border-gray-200 p-5 space-y-2">
                          {allTasks.length === 0 && (
                            <p className="text-sm text-gray-400 py-1">{t('cleaning.noTasksTitle')}</p>
                          )}
                          {allTasks.map((task: CleaningTask) => (
                            <div key={task.id} className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
                              <span className="text-base flex-shrink-0">☑️</span>
                              {editingTaskId === task.id ? (
                                <>
                                  <input
                                    autoFocus
                                    type="text"
                                    value={editTaskName}
                                    onChange={(e) => setEditTaskName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && editTaskName.trim()) renameTaskMutation.mutate({ id: task.id, name: editTaskName.trim() });
                                      if (e.key === 'Escape') setEditingTaskId(null);
                                    }}
                                    className="flex-1 border border-gray-300 rounded-lg px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                                  />
                                  <button
                                    onClick={() => { if (editTaskName.trim()) renameTaskMutation.mutate({ id: task.id, name: editTaskName.trim() }); }}
                                    disabled={!editTaskName.trim() || renameTaskMutation.isPending}
                                    className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                                  >{renameTaskMutation.isPending ? '…' : '✓'}</button>
                                  <button onClick={() => setEditingTaskId(null)} className="px-2 py-1.5 text-gray-500 hover:bg-gray-100 rounded-lg text-sm">✕</button>
                                </>
                              ) : (
                                <>
                                  <span className="flex-1 text-sm font-medium text-gray-700">{task.name}</span>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{t('cleaning.daily')}</span>
                                  <button
                                    onClick={() => { setEditingTaskId(task.id); setEditTaskName(task.name); }}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => { if (confirm(t('cleaning.confirmDeleteTask'))) deleteTaskMutation.mutate(task.id); }}
                                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          ))}

                          {/* Add task */}
                          {addingTaskFor === sector.id ? (
                            <div className="flex items-center gap-2 pt-1">
                              <input
                                autoFocus
                                type="text"
                                value={newTaskName}
                                onChange={(e) => setNewTaskName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && newTaskName.trim()) addTaskMutation.mutate({ sectorId: sector.id, name: newTaskName.trim() });
                                  if (e.key === 'Escape') { setAddingTaskFor(null); setNewTaskName(''); }
                                }}
                                placeholder={t('cleaning.taskNamePlaceholder')}
                                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                              />
                              <button
                                onClick={() => { if (newTaskName.trim()) addTaskMutation.mutate({ sectorId: sector.id, name: newTaskName.trim() }); }}
                                disabled={!newTaskName.trim() || addTaskMutation.isPending}
                                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
                              >{addTaskMutation.isPending ? '…' : '✓'}</button>
                              <button onClick={() => { setAddingTaskFor(null); setNewTaskName(''); }} className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm">✕</button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setAddingTaskFor(sector.id); setNewTaskName(''); }}
                              className="flex items-center gap-2 text-sm text-orange-500 hover:text-orange-700 font-semibold px-3 py-2 rounded-lg hover:bg-orange-50 transition-colors border-2 border-dashed border-orange-200 hover:border-orange-400 w-full justify-center mt-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              {t('cleaning.addTask')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* ── Add new sector ── */}
                <div className="bg-white rounded-xl shadow p-5 space-y-3">
                  <h3 className="font-bold text-gray-900">{t('cleaning.addSector')}</h3>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowIconPicker(!showIconPicker)}
                      className="flex-shrink-0 w-11 h-11 flex items-center justify-center text-xl bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors border-2 border-transparent focus:border-orange-400"
                    >
                      {newSectorIcon}
                    </button>
                    <input
                      type="text"
                      value={newSectorName}
                      onChange={(e) => setNewSectorName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && newSectorName.trim()) createSectorMutation.mutate({ name: newSectorName.trim(), icon: newSectorIcon }); }}
                      placeholder={t('cleaning.sectorNamePlaceholder')}
                      className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  {showIconPicker && (
                    <div className="grid grid-cols-8 gap-1 p-2 bg-gray-50 border border-gray-200 rounded-xl">
                      {SECTOR_ICONS.map((icon) => (
                        <button key={icon} type="button"
                          onClick={() => { setNewSectorIcon(icon); setShowIconPicker(false); }}
                          className={`text-xl p-1.5 rounded-lg hover:bg-orange-100 transition ${newSectorIcon === icon ? 'bg-orange-200 ring-2 ring-orange-400' : ''}`}
                        >{icon}</button>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => { if (newSectorName.trim()) createSectorMutation.mutate({ name: newSectorName.trim(), icon: newSectorIcon }); }}
                    disabled={!newSectorName.trim() || createSectorMutation.isPending}
                    className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold transition-colors disabled:opacity-50"
                  >
                    {createSectorMutation.isPending ? '...' : `+ ${t('cleaning.addSector')}`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
